import { create } from 'zustand';

type FormRegistration = {
  id: string;
  isDirty: boolean;
};

type UnsavedChangesState = {
  registrations: Map<string, FormRegistration>;
  isModalVisible: boolean;
  pendingAction: (() => void) | undefined;
  pendingFormId: string | undefined;

  register: (id: string) => void;
  unregister: (id: string) => void;
  setDirty: (id: string, isDirty: boolean) => void;
  hasDirtyForms: () => boolean;
  isFormDirty: (id: string) => boolean;
  guard: (action: () => void) => void;
  guardForForm: (formId: string, action: () => void) => void;
  confirmDiscard: () => void;
  cancelDiscard: () => void;
};

export const useUnsavedChangesStore = create<UnsavedChangesState>(
  (set, get) => ({
    registrations: new Map(),
    isModalVisible: false,
    pendingAction: undefined,
    pendingFormId: undefined,

    register: (id: string) => {
      set((state) => {
        const newRegistrations = new Map(state.registrations);
        newRegistrations.set(id, { id, isDirty: false });
        return { registrations: newRegistrations };
      });
    },

    unregister: (id: string) => {
      set((state) => {
        const newRegistrations = new Map(state.registrations);
        newRegistrations.delete(id);
        return { registrations: newRegistrations };
      });
    },

    setDirty: (id: string, isDirty: boolean) => {
      set((state) => {
        const registration = state.registrations.get(id);
        if (!registration) return state;

        const newRegistrations = new Map(state.registrations);
        newRegistrations.set(id, { ...registration, isDirty });
        return { registrations: newRegistrations };
      });
    },

    hasDirtyForms: () => {
      const { registrations } = get();
      return Array.from(registrations.values()).some((reg) => reg.isDirty);
    },

    isFormDirty: (id: string) => {
      const { registrations } = get();
      return registrations.get(id)?.isDirty ?? false;
    },

    guard: (action: () => void) => {
      const { hasDirtyForms, isModalVisible } = get();
      if (isModalVisible) return;

      if (hasDirtyForms()) {
        set({
          isModalVisible: true,
          pendingAction: action,
          pendingFormId: undefined,
        });
      } else {
        action();
      }
    },

    guardForForm: (formId: string, action: () => void) => {
      const { isFormDirty, isModalVisible } = get();
      if (isModalVisible) return;

      if (isFormDirty(formId)) {
        set({
          isModalVisible: true,
          pendingAction: action,
          pendingFormId: formId,
        });
      } else {
        action();
      }
    },

    confirmDiscard: () => {
      const { pendingAction, pendingFormId, registrations } = get();
      const clearedRegistrations = new Map(registrations);

      if (pendingFormId) {
        // Only clear the specific form's dirty state
        const reg = clearedRegistrations.get(pendingFormId);
        if (reg) {
          clearedRegistrations.set(pendingFormId, { ...reg, isDirty: false });
        }
      } else {
        // Global guard: clear all dirty states (navigation away from page)
        clearedRegistrations.forEach((reg, id) => {
          clearedRegistrations.set(id, { ...reg, isDirty: false });
        });
      }

      set({
        isModalVisible: false,
        pendingAction: undefined,
        pendingFormId: undefined,
        registrations: clearedRegistrations,
      });
      if (pendingAction) {
        pendingAction();
      }
    },

    cancelDiscard: () => {
      set({
        isModalVisible: false,
        pendingAction: undefined,
        pendingFormId: undefined,
      });
    },
  }),
);
